<template>
  <section class="page">
    <div class="page__head">
      <h2 class="page__title">{{ pageTitle }}</h2>
      <div class="toolbar">
        <input
          v-model="q"
          class="toolbar__search"
          type="text"
          :placeholder="$t('bsfp.searchPh')"
          :aria-label="$t('bsfp.searchPh')"
        />
      </div>
    </div>

    <div class="mpo-container">
      <!-- Левая часть - список документов -->
      <div class="documents-list">
        <div
          v-for="doc in filteredDocs"
          :key="doc.id"
          class="document-item"
          :class="{ 'is-active': selectedDocId === doc.id }"
          @click="selectDoc(doc)"
        >
          <div class="doc-icon">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
          </div>
          <div class="doc-info">
            <p class="doc-name">{{ doc.title }}</p>
            <p class="doc-type">{{ doc.file_name || 'PDF Document' }}</p>
          </div>
        </div>

        <div v-if="!loading && !filteredDocs.length" class="empty">
          {{ $t('bsfp.noDocs') }}
        </div>
      </div>

      <!-- Правая часть - открыть PDF -->
      <div class="pdf-viewer">
        <div v-if="selectedDocHref" class="pdf-actions">
          <div class="pdf-meta">
            <div class="pdf-title">{{ selectedDoc?.title || $t('bsfp.docFallback') }}</div>
            <div class="pdf-filename">{{ selectedDoc?.file_name || '' }}</div>
          </div>
          <div class="pdf-buttons">
            <a
              class="pdf-btn"
              :href="selectedDocHref"
              target="_blank"
              rel="noopener"
            >{{ $t('bsfp.openPdf') }}</a>
            <a
              class="pdf-btn pdf-btn--secondary"
              :href="selectedDocHref"
              download
            >{{ $t('bsfp.download') }}</a>
          </div>
        </div>
        <div v-else class="no-pdf">
          <p>{{ $t('bsfp.noPdf') }}</p>
          <p class="hint">{{ $t('bsfp.pdfHint') }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { http, getBackendOrigin } from '@/api/http'

function toAbsoluteMediaUrl(url) {
  if (!url) return null
  const origin = getBackendOrigin()
  if (!origin) return url
  try {
    const u = new URL(url, origin)
    // If backend produced an absolute localhost URL, replace with detected backend origin.
    if (u.hostname === 'localhost' || u.hostname === '127.0.0.1') {
      const o = new URL(origin)
      u.protocol = o.protocol
      u.host = o.host
    }
    return u.toString()
  } catch (e) {
    return url
  }
}

function toSameOriginMediaHref(rawUrl) {
  if (!rawUrl) return null
  // Prefer /media/... so Vue devServer proxy serves it from the same origin (:8081).
  try {
    const origin = getBackendOrigin() || window.location.origin
    const abs = toAbsoluteMediaUrl(rawUrl)
    const u = new URL(abs, origin)
    const marker = '/media/'
    const idx = u.pathname.indexOf(marker)
    if (idx !== -1) {
      return u.pathname.slice(idx)
    }
    // Fallback: if backend already returned relative URL like /media/...
    if (String(rawUrl).startsWith('/media/')) return String(rawUrl)
    return abs
  } catch (e) {
    if (String(rawUrl).startsWith('/media/')) return String(rawUrl)
    return rawUrl
  }
}

export default {
  name: 'BsfpLiteratureView',
  data() {
    return {
      q: '',
      loading: false,
      docs: [],
      selectedDocId: null,
    }
  },
  computed: {
    pageTitle() {
      const meta = this.$route?.meta || {}
      if (meta.headerTitleI18n) return this.$t(meta.headerTitleI18n)
      return meta.headerTitle || this.$t('bsfp.titles.literature')
    },
    filteredDocs() {
      const qq = this.q.trim().toLowerCase()
      if (!qq) return this.docs
      return this.docs.filter(d => {
        const hay = `${d.title || ''} ${d.file_name || ''}`.toLowerCase()
        return hay.includes(qq)
      })
    },
    selectedDoc() {
      return this.docs.find(d => d.id === this.selectedDocId) || null
    },
    selectedDocHref() {
      const doc = this.selectedDoc
      const raw = doc?.file_url || doc?.file || null
      return toSameOriginMediaHref(raw)
    },
  },
  async mounted() {
    await this.loadDocs()
  },
  methods: {
    async loadDocs() {
      this.loading = true
      try {
        const r = await http.get('bsfp/literature/')
        const data = r?.data
        const items = Array.isArray(data) ? data : (Array.isArray(data?.results) ? data.results : [])
        this.docs = items
        if (items.length && this.selectedDocId == null) this.selectedDocId = items[0].id
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to load BSFP literature docs', e)
        this.docs = []
      } finally {
        this.loading = false
      }
    },
    selectDoc(doc) {
      this.selectedDocId = doc?.id ?? null
    },
  },
}
</script>

<style scoped>
.page {
  display: flex;
  flex-direction: column;
  height: 100vh;  
  padding: 2rem;
  background: #f5f7fa;
}

.page__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}
.page__head .page__title { flex: 1 1 auto; min-width: 0; margin-bottom: 0; }

.page__title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a202c;
  margin-bottom: 2rem;
}

.toolbar {
  flex: 0 0 auto;
}
.toolbar__search {
  width: 320px;
  max-width: 100%;
  height: 40px;
  padding: 6px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  background: #fff;
}
.toolbar__search:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
}
.mpo-container {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 2rem;
  flex: 1;
  min-height: 0;
  height: 100%;
}


.documents-list {
  background: white;
  border-radius: 8px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
}

.document-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  border-radius: 6px;
  background: #f9fafb;
  border: 2px solid #e5e7eb;
  cursor: pointer;
  transition: all 0.2s ease;
}
.document-item.is-active {
  border-color: #3b82f6;
  background: #f0f4f8;
}

.document-item:hover {
  background: #f0f4f8;
  border-color: #3b82f6;
}

.doc-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 6px;
  background: #dbeafe;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1e40af;
}

.doc-info {
  flex: 1;
  min-width: 0;
}

.doc-name {
  font-weight: 600;
  color: #1a202c;
  font-size: 0.875rem;
  margin: 0;
}

.doc-type {
  font-size: 0.75rem;
  color: #6b7280;
  margin: 0.25rem 0 0;
}

.pdf-viewer {
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  display: flex;
  flex-direction: column;

  height: 100%;
  min-height: 0;
}

.pdf-actions {
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
  padding: 24px;
  flex: 1;
}

.pdf-meta {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.pdf-title {
  font-weight: 700;
  color: #111827;
  font-size: 16px;
}

.pdf-filename {
  color: #6b7280;
  font-size: 13px;
}

.pdf-buttons {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.pdf-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  border: 1px solid #3b82f6;
  background: #3b82f6;
  color: #ffffff;
  font-weight: 700;
  font-size: 14px;
  text-decoration: none;
  cursor: pointer;
}

.pdf-btn:hover {
  filter: brightness(0.95);
}

.pdf-btn--secondary {
  background: #ffffff;
  color: #1d4ed8;
}

.no-pdf {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #6b7280;
  font-size: 0.95rem;
  text-align: center;
  gap: 1rem;
}

.no-pdf p {
  margin: 0;
}

.no-pdf code {
  background: #f3f4f6;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-family: monospace;
  color: #1a202c;
  font-size: 0.85rem;
}

.hint {
  max-width: 520px;
  font-size: 0.9rem;
  color: #6b7280;
}

.empty {
  padding: 12px;
  color: #6b7280;
  font-size: 0.9rem;
}
</style>